import { useCallback, useEffect, useRef, useState } from "react";
import { saveProctoringSignals } from "../services/proctoringService";
import { calculateIntegrityScore, getIntegrityLevel } from "../utils/integrityScoring";

const emptySignals = {
  tabSwitchCount: 0,
  windowBlurCount: 0,
  fullscreenViolations: 0,
  copyPasteAttempts: 0,
  multipleFaceDetected: false,
  noFaceDetectedDuration: 0,
  webcamEnabled: false,
  timestamps: []
};

function appendTimestamp(signals, type, details = {}) {
  return {
    ...signals,
    timestamps: [
      ...(signals.timestamps || []).slice(-80),
      { type, at: new Date().toISOString(), ...details }
    ]
  };
}

function loadMediaPipeFaceDetection() {
  if (window.FaceDetection) {
    return Promise.resolve(window.FaceDetection);
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-mediapipe-face-detection]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.FaceDetection));
      existing.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    // Pinned, not "latest" — see the matching comment in
    // useCampaignProctoring.js's loadFaceMesh() for why.
    script.src = "https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@0.4.1646425229/face_detection.js";
    script.async = true;
    script.dataset.mediapipeFaceDetection = "true";
    script.onload = () => resolve(window.FaceDetection);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

export default function useProctoringSignals({ enabled, interviewId, mediaStream }) {
  const [signals, setSignals] = useState(emptySignals);
  const [warning, setWarning] = useState("");
  const [faceStatus, setFaceStatus] = useState("idle");
  const [persistError, setPersistError] = useState("");

  const signalsRef = useRef(emptySignals);
  const videoRef = useRef(null);
  const detectorRef = useRef(null);
  const faceTimerRef = useRef(null);
  const noFaceStartedAtRef = useRef(null);
  const persistTimerRef = useRef(null);
  const lastPersistedRef = useRef("");

  const updateSignals = useCallback((updater, warningText = "") => {
    setSignals((current) => {
      const nextBase = typeof updater === "function" ? updater(current) : updater;
      const next = {
        ...nextBase,
        integrityScore: calculateIntegrityScore(nextBase),
      };
      next.integrityLevel = getIntegrityLevel(next.integrityScore);
      signalsRef.current = next;
      return next;
    });
    if (warningText) {
      setWarning(warningText);
      window.clearTimeout(updateSignals.warningTimer);
      updateSignals.warningTimer = window.setTimeout(() => setWarning(""), 5000);
    }
  }, []);

  const persist = useCallback(async (force = false) => {
    if (!enabled || !interviewId) return;
    const payload = signalsRef.current;
    const serialized = JSON.stringify(payload);
    if (!force && serialized === lastPersistedRef.current) return;

    try {
      await saveProctoringSignals(interviewId, payload);
      lastPersistedRef.current = serialized;
      setPersistError("");
    } catch (err) {
      console.warn("Unable to persist proctoring signals:", err);
      setPersistError("Integrity signals are temporarily not syncing.");
    }
  }, [enabled, interviewId]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onVisibility = () => {
      if (document.hidden) {
        updateSignals((current) => appendTimestamp({
          ...current,
          tabSwitchCount: (current.tabSwitchCount || 0) + 1
        }, "tab_hidden"), "Please keep this interview tab active.");
      }
    };

    const onBlur = () => {
      updateSignals((current) => appendTimestamp({
        ...current,
        windowBlurCount: (current.windowBlurCount || 0) + 1
      }, "window_blur"), "Please keep the interview window focused.");
    };

    const onFullscreen = () => {
      if (!document.fullscreenElement) {
        updateSignals((current) => appendTimestamp({
          ...current,
          fullscreenViolations: (current.fullscreenViolations || 0) + 1
        }, "fullscreen_exit"), "Fullscreen was exited during the interview.");
      }
    };

    const onCopyPaste = (event) => {
      updateSignals((current) => appendTimestamp({
        ...current,
        copyPasteAttempts: (current.copyPasteAttempts || 0) + 1
      }, event.type), "Copy/paste is not allowed during this interview.");
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFullscreen);
    document.addEventListener("copy", onCopyPaste);
    document.addEventListener("paste", onCopyPaste);
    document.addEventListener("cut", onCopyPaste);

    persistTimerRef.current = window.setInterval(() => persist(false), 5000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFullscreen);
      document.removeEventListener("copy", onCopyPaste);
      document.removeEventListener("paste", onCopyPaste);
      document.removeEventListener("cut", onCopyPaste);
      window.clearInterval(persistTimerRef.current);
      persist(true);
    };
  }, [enabled, persist, updateSignals]);

  useEffect(() => {
    if (!enabled) return;
    const webcamEnabled = Boolean(mediaStream?.getVideoTracks?.().some((track) => track.readyState === "live" && track.enabled));
    updateSignals((current) => appendTimestamp({
      ...current,
      webcamEnabled
    }, webcamEnabled ? "webcam_enabled" : "webcam_disabled"));
  }, [enabled, mediaStream, updateSignals]);

  useEffect(() => {
    if (!enabled || !mediaStream) return undefined;

    let cancelled = false;
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = mediaStream;
    videoRef.current = video;

    async function startFaceDetection() {
      try {
        setFaceStatus("loading");
        await video.play();
        const FaceDetection = await loadMediaPipeFaceDetection();
        if (cancelled || !FaceDetection) return;

        const detector = new FaceDetection({
          // Must match the pinned version in loadMediaPipeFaceDetection() above.
          locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@0.4.1646425229/${file}`
        });
        detector.setOptions({
          model: "short",
          minDetectionConfidence: 0.55
        });
        detector.onResults((results) => {
          const count = results.detections?.length || 0;
          const now = Date.now();
          setFaceStatus(count > 1 ? "multiple" : count === 1 ? "single" : "none");

          if (count === 0) {
            if (!noFaceStartedAtRef.current) noFaceStartedAtRef.current = now;
            const noFaceSeconds = Math.round((now - noFaceStartedAtRef.current) / 1000);
            updateSignals((current) => appendTimestamp({
              ...current,
              noFaceDetectedDuration: Math.max(current.noFaceDetectedDuration || 0, noFaceSeconds)
            }, "no_face", { duration: noFaceSeconds }), noFaceSeconds >= 10 ? "No face is visible. Please stay in frame." : "");
          } else {
            noFaceStartedAtRef.current = null;
          }

          if (count > 1) {
            updateSignals((current) => appendTimestamp({
              ...current,
              multipleFaceDetected: true
            }, "multiple_faces", { count }), "Multiple faces detected. Please interview alone.");
          }
        });
        detectorRef.current = detector;
        setFaceStatus("active");

        faceTimerRef.current = window.setInterval(() => {
          if (!cancelled && video.readyState >= 2) {
            detector.send({ image: video }).catch(() => {});
          }
        }, 2500);
      } catch (err) {
        console.warn("Face detection unavailable:", err);
        setFaceStatus("unavailable");
      }
    }

    startFaceDetection();

    return () => {
      cancelled = true;
      window.clearInterval(faceTimerRef.current);
      detectorRef.current?.close?.();
      video.pause();
      video.srcObject = null;
    };
  }, [enabled, mediaStream, updateSignals]);

  return {
    signals,
    warning,
    faceStatus,
    persistError,
    integrityScore: signals.integrityScore ?? 100,
    integrityLevel: signals.integrityLevel || "high",
    flush: () => persist(true)
  };
}
